import CRMEnterprisePreset from '../../../CRMEnterprisePreset';

type ThemePrimaryScaleKey = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;

type ThemeSurfaceScaleKey = 0 | ThemePrimaryScaleKey;

export type ThemePrimaryPalette = Record<ThemePrimaryScaleKey, string>;
export type ThemeSurfacePalette = Record<ThemeSurfaceScaleKey, string>;
type ThemeMode = 'light' | 'dark';

export interface ThemeSurfacePalettePair {
  light: ThemeSurfacePalette;
  dark: ThemeSurfacePalette;
}

interface ThemeModeDefinition {
  primary?: ThemePrimaryPalette;
  surface?: ThemeSurfacePalette;
}

interface ThemeDefinition {
  label?: string;
  description?: string;
  light?: ThemeModeDefinition;
  dark?: ThemeModeDefinition;
}

interface ThemeFamilyCatalogEntry {
  label: string;
  descriptionPrefix: string;
  themes: readonly ThemeDefinition[];
}

export interface ThemeModeConfig {
  primary: ThemePrimaryPalette;
  surface: ThemeSurfacePalette;
}

export interface ThemeModesConfig {
  light: ThemeModeConfig;
  dark: ThemeModeConfig;
}

type CRMThemeSemanticShape = {
  primary?: ThemePrimaryPalette;
  colorScheme?: {
    light?: {
      surface?: ThemeSurfacePalette;
    };
    dark?: {
      surface?: ThemeSurfacePalette;
    };
  };
};

const CRM_THEME_SEMANTIC = ((CRMEnterprisePreset as { semantic?: CRMThemeSemanticShape }).semantic ??
  {}) as CRMThemeSemanticShape;

export const DEFAULT_THEME_PRIMARY_PALETTE = {
  ...(CRM_THEME_SEMANTIC.primary ?? {}),
} as ThemePrimaryPalette;

export const DEFAULT_THEME_SURFACE_PALETTE: ThemeSurfacePalettePair = {
  light: {
    ...(CRM_THEME_SEMANTIC.colorScheme?.light?.surface ?? {}),
  } as ThemeSurfacePalette,
  dark: {
    ...(CRM_THEME_SEMANTIC.colorScheme?.dark?.surface ?? {}),
  } as ThemeSurfacePalette,
};

const DEFAULT_THEME_MODES: ThemeModesConfig = {
  light: {
    primary: DEFAULT_THEME_PRIMARY_PALETTE,
    surface: DEFAULT_THEME_SURFACE_PALETTE.light,
  },
  dark: {
    primary: DEFAULT_THEME_PRIMARY_PALETTE,
    surface: DEFAULT_THEME_SURFACE_PALETTE.dark,
  },
};

function resolveThemeModes(theme: ThemeDefinition): ThemeModesConfig {
  const lightPrimary = theme.light?.primary ?? theme.dark?.primary ?? DEFAULT_THEME_MODES.light.primary;
  const darkPrimary = theme.dark?.primary ?? theme.light?.primary ?? DEFAULT_THEME_MODES.dark.primary;

  return {
    light: {
      primary: lightPrimary,
      surface: theme.light?.surface ?? DEFAULT_THEME_MODES.light.surface,
    },
    dark: {
      primary: darkPrimary,
      surface: theme.dark?.surface ?? DEFAULT_THEME_MODES.dark.surface,
    },
  };
}

const ORIGINALS_SURFACE_1: ThemeSurfacePalettePair = {
  light: {
    0: '#ffffff',
    50: '{slate.50}',
    100: '{slate.100}',
    200: '{slate.200}',
    300: '{slate.300}',
    400: '{slate.400}',
    500: '{slate.500}',
    600: '{slate.600}',
    700: '{slate.700}',
    800: '{slate.800}',
    900: '{slate.900}',
    950: '{slate.950}',
  },
  dark: {
    0: '#ffffff',
    50: '{zinc.50}',
    100: '{zinc.100}',
    200: '{zinc.200}',
    300: '{zinc.300}',
    400: '{zinc.400}',
    500: '{zinc.500}',
    600: '{zinc.600}',
    700: '{zinc.700}',
    800: '{zinc.800}',
    900: '{zinc.900}',
    950: '{zinc.950}',
  },
};

const ORIGINALS_SURFACE_2: ThemeSurfacePalettePair = {
  light: {
    0: '#fcfdff',
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617'
  },
  dark: {
    0: '#ffffff',
    50: '#f5f7fb',
    100: '#e7ebf2',
    200: '#cfd6e2',
    300: '#aab5c7',
    400: '#7f8ba0',
    500: '#5f6b81',
    600: '#445065',
    700: '#31384a',
    800: '#1e2431',
    900: '#121722',
    950: '#0a0d14'
  },
};

const MONO_MIST_SURFACE: ThemeSurfacePalettePair = {
  light: {
    0: '#fbfdff',
    50: '#f5f8fb',
    100: '#eaf0f6',
    200: '#d8e1eb',
    300: '#bcc8d8',
    400: '#95a5ba',
    500: '#70839b',
    600: '#55667d',
    700: '#404f64',
    800: '#2c384b',
    900: '#1a2333',
    950: '#0d131f',
  },
  dark: {
    0: '#ffffff',
    50: '#f2f5fa',
    100: '#e1e7f0',
    200: '#c3cedc',
    300: '#9ba9bc',
    400: '#738196',
    500: '#566278',
    600: '#3f4a60',
    700: '#2d3548',
    800: '#1d2331',
    900: '#111621',
    950: '#080b12',
  },
};

const MONO_DEEP_SURFACE: ThemeSurfacePalettePair = {
  light: {
    0: '#fafdff',
    50: '#f2f6fb',
    100: '#e5edf6',
    200: '#d0dceb',
    300: '#aec0d5',
    400: '#859bb5',
    500: '#627892',
    600: '#495f78',
    700: '#34485f',
    800: '#223347',
    900: '#141f2d',
    950: '#0a1018',
  },
  dark: {
    0: '#ffffff',
    50: '#edf3fb',
    100: '#d7e1ed',
    200: '#b6c4d5',
    300: '#8fa1b8',
    400: '#687b93',
    500: '#4d6076',
    600: '#39495d',
    700: '#283545',
    800: '#19222f',
    900: '#0e1520',
    950: '#050911',
  },
};

const MONO_WIDE_SKY_SURFACE: ThemeSurfacePalettePair = {
  light: {
    0: '#fbfdff',
    50: '#f2f7fc',
    100: '#e3edf6',
    200: '#cad9e8',
    300: '#a6bdd2',
    400: '#7e98b3',
    500: '#5d7490',
    600: '#445a74',
    700: '#314359',
    800: '#202f41',
    900: '#121d2b',
    950: '#09111b',
  },
  dark: {
    0: '#ffffff',
    50: '#eef4fb',
    100: '#d9e4f1',
    200: '#bacbdd',
    300: '#8fa5bd',
    400: '#687f97',
    500: '#4d6178',
    600: '#39485d',
    700: '#293445',
    800: '#1a2231',
    900: '#0f1521',
    950: '#060a11',
  },
};

const MONO_WIDE_NIGHT_SURFACE: ThemeSurfacePalettePair = {
  light: {
    0: '#fafdff',
    50: '#f0f6fc',
    100: '#e0ebf5',
    200: '#c3d4e4',
    300: '#98b2c8',
    400: '#6c89a5',
    500: '#4d6985',
    600: '#39516c',
    700: '#283c53',
    800: '#19293d',
    900: '#0f1826',
    950: '#050b13',
  },
  dark: {
    0: '#ffffff',
    50: '#edf4fc',
    100: '#d4e1ef',
    200: '#b0c3d6',
    300: '#8299b4',
    400: '#5f7692',
    500: '#455a74',
    600: '#33465c',
    700: '#233242',
    800: '#151f2c',
    900: '#0b121d',
    950: '#040811',
  },
};

export const THEME_PRIMARY_THEME_CATALOG = {
  // Add a new theme by appending one object to a `themes` array.
  // Use plain objects directly in each entry: `label`, `light`, `dark`.
  originals: {
    label: 'Originals',
    descriptionPrefix: 'Zestaw oryginalny',
    themes: [
      {
        label: 'Originals 01',
        light: {
            primary: {
              50: '#eef9fd',
              100: '#d9f0fa',
              200: '#bce4f5',
              300: '#8fd1ec',
              400: '#56b8df',
              500: '#1aa6e6',
              600: '#1493cc',
              700: '#0e80b3',
              800: '#096d99',
              900: '#055a80',
              950: '#033d59',
            },
            surface: ORIGINALS_SURFACE_1.light,
        },
        dark: {
            surface: ORIGINALS_SURFACE_1.dark,
        },
      },
      {
        label: 'Originals 02',
        light: {
          primary: {
            50: '#f4f8fb',
            100: '#e8f0f7',
            200: '#d3e1ef',
            300: '#b4cde3',
            400: '#89aecf',
            500: '#5f88af',
            600: '#496f95',
            700: '#3b5a78',
            800: '#314a63',
            900: '#293d52',
            950: '#182434'
          },
          surface: ORIGINALS_SURFACE_2.light,
        },
        dark: {
          surface: ORIGINALS_SURFACE_2.dark
        }
      }
    ],
  },
  mono: {
    label: 'Mono',
    descriptionPrefix: 'Skala neutralna',
    themes: [
      {
        label: 'Mono 01',
        light: {
            primary: { 50: '#f9fbfd', 100: '#f1f6fb', 200: '#e3edf6', 300: '#cfdfee', 400: '#b2cae1', 500: '#91b0cf', 600: '#7494b4', 700: '#5c7897', 800: '#4a627c', 900: '#3d5167', 950: '#263241' },
            surface: DEFAULT_THEME_SURFACE_PALETTE.light,
        },
        dark: {
            primary: { 50: '#f9fbfd', 100: '#f1f6fb', 200: '#e3edf6', 300: '#cfdfee', 400: '#b2cae1', 500: '#91b0cf', 600: '#7494b4', 700: '#5c7897', 800: '#4a627c', 900: '#3d5167', 950: '#263241' },
            surface: DEFAULT_THEME_SURFACE_PALETTE.dark,
        },
      },
      {
        label: 'Mono 02',
        light: {
            primary: { 50: '#f7fafd', 100: '#eef5fb', 200: '#deebf7', 300: '#c7dcf0', 400: '#a7c5e2', 500: '#86a9cf', 600: '#698db4', 700: '#547397', 800: '#465f7d', 900: '#3a5069', 950: '#233244' },
            surface: MONO_MIST_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f7fafd', 100: '#eef5fb', 200: '#deebf7', 300: '#c7dcf0', 400: '#a7c5e2', 500: '#86a9cf', 600: '#698db4', 700: '#547397', 800: '#465f7d', 900: '#3a5069', 950: '#233244' },
            surface: MONO_MIST_SURFACE.dark,
        },
      },
      {
        label: 'Mono 03',
        light: {
            primary: { 50: '#f6f9fc', 100: '#ebf3f9', 200: '#d9e8f3', 300: '#bfd8ea', 400: '#9ebfde', 500: '#7ca3cc', 600: '#6287b2', 700: '#4f6d95', 800: '#425a7a', 900: '#374b66', 950: '#223042' },
            surface: MONO_MIST_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f6f9fc', 100: '#ebf3f9', 200: '#d9e8f3', 300: '#bfd8ea', 400: '#9ebfde', 500: '#7ca3cc', 600: '#6287b2', 700: '#4f6d95', 800: '#425a7a', 900: '#374b66', 950: '#223042' },
            surface: MONO_MIST_SURFACE.dark,
        },
      },
      {
        label: 'Mono 04',
        light: {
            primary: { 50: '#f5f8fb', 100: '#e9f0f7', 200: '#d5e3ef', 300: '#b7d0e4', 400: '#92b4d4', 500: '#6f97be', 600: '#597da3', 700: '#496687', 800: '#3d556f', 900: '#34475e', 950: '#1f2c3b' },
            surface: MONO_MIST_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f5f8fb', 100: '#e9f0f7', 200: '#d5e3ef', 300: '#b7d0e4', 400: '#92b4d4', 500: '#6f97be', 600: '#597da3', 700: '#496687', 800: '#3d556f', 900: '#34475e', 950: '#1f2c3b' },
            surface: MONO_MIST_SURFACE.dark,
        },
      },
      {
        label: 'Mono 05',
        light: {
            primary: { 50: '#f4f8fb', 100: '#e8f0f7', 200: '#d3e1ef', 300: '#b4cde3', 400: '#89aecf', 500: '#5f88af', 600: '#496f95', 700: '#3b5a78', 800: '#314a63', 900: '#293d52', 950: '#182434' },
            surface: MONO_MIST_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f4f8fb', 100: '#e8f0f7', 200: '#d3e1ef', 300: '#b4cde3', 400: '#89aecf', 500: '#5f88af', 600: '#496f95', 700: '#3b5a78', 800: '#314a63', 900: '#293d52', 950: '#182434' },
            surface: MONO_MIST_SURFACE.dark,
        },
      },
      {
        label: 'Mono 06',
        light: {
            primary: { 50: '#f4f7fa', 100: '#e6eef5', 200: '#cfdeec', 300: '#adc7dd', 400: '#7ea6c7', 500: '#547fa8', 600: '#44688d', 700: '#385470', 800: '#30465d', 900: '#293b4f', 950: '#182230' },
            surface: MONO_DEEP_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f4f7fa', 100: '#e6eef5', 200: '#cfdeec', 300: '#adc7dd', 400: '#7ea6c7', 500: '#547fa8', 600: '#44688d', 700: '#385470', 800: '#30465d', 900: '#293b4f', 950: '#182230' },
            surface: MONO_DEEP_SURFACE.dark,
        },
      },
      {
        label: 'Mono 07',
        light: {
            primary: { 50: '#f3f7fa', 100: '#e4edf4', 200: '#ccddeb', 300: '#a8c3dc', 400: '#759ec4', 500: '#4b76a4', 600: '#3d618c', 700: '#334f72', 800: '#2c425f', 900: '#26384f', 950: '#151f30' },
            surface: MONO_DEEP_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f3f7fa', 100: '#e4edf4', 200: '#ccddeb', 300: '#a8c3dc', 400: '#759ec4', 500: '#4b76a4', 600: '#3d618c', 700: '#334f72', 800: '#2c425f', 900: '#26384f', 950: '#151f30' },
            surface: MONO_DEEP_SURFACE.dark,
        },
      },
      {
        label: 'Mono 08',
        light: {
            primary: { 50: '#f2f6fa', 100: '#e2ebf3', 200: '#c7d9e8', 300: '#9fbad6', 400: '#6e95bc', 500: '#456e9b', 600: '#395b82', 700: '#304c6a', 800: '#293f59', 900: '#23354b', 950: '#131d2d' },
            surface: MONO_DEEP_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f2f6fa', 100: '#e2ebf3', 200: '#c7d9e8', 300: '#9fbad6', 400: '#6e95bc', 500: '#456e9b', 600: '#395b82', 700: '#304c6a', 800: '#293f59', 900: '#23354b', 950: '#131d2d' },
            surface: MONO_DEEP_SURFACE.dark,
        },
      },
      {
        label: 'Mono 09',
        light: {
            primary: { 50: '#f1f5f9', 100: '#dfe9f2', 200: '#c1d6e6', 300: '#96b8d0', 400: '#628daf', 500: '#3c678f', 600: '#315677', 700: '#294762', 800: '#233b53', 900: '#1f3348', 950: '#111d2c' },
            surface: MONO_DEEP_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f1f5f9', 100: '#dfe9f2', 200: '#c1d6e6', 300: '#96b8d0', 400: '#628daf', 500: '#3c678f', 600: '#315677', 700: '#294762', 800: '#233b53', 900: '#1f3348', 950: '#111d2c' },
            surface: MONO_DEEP_SURFACE.dark,
        },
      },
      {
        label: 'Mono 10',
        light: {
            primary: { 50: '#eff4f8', 100: '#dce7f0', 200: '#bbd0e1', 300: '#8fb0c9', 400: '#5d86aa', 500: '#365f88', 600: '#2d5073', 700: '#26425f', 800: '#20374f', 900: '#1d3045', 950: '#0f1827' },
            surface: MONO_DEEP_SURFACE.light,
        },
        dark: {
            primary: { 50: '#eff4f8', 100: '#dce7f0', 200: '#bbd0e1', 300: '#8fb0c9', 400: '#5d86aa', 500: '#365f88', 600: '#2d5073', 700: '#26425f', 800: '#20374f', 900: '#1d3045', 950: '#0f1827' },
            surface: MONO_DEEP_SURFACE.dark,
        },
      },
    ],
  },
  'mono-wide': {
    label: 'Mono Wide',
    descriptionPrefix: 'Szerszy kontrast',
    themes: [
      {
        label: 'Mono Wide 01',
        light: {
            primary: { 50: '#fbfdff', 100: '#edf6ff', 200: '#d6eaff', 300: '#b5d7ff', 400: '#7fb7ff', 500: '#458fe8', 600: '#2f74cb', 700: '#245ca4', 800: '#214d85', 900: '#1f426d', 950: '#142746' },
            surface: MONO_WIDE_SKY_SURFACE.light,
        },
        dark: {
            primary: { 50: '#fbfdff', 100: '#edf6ff', 200: '#d6eaff', 300: '#b5d7ff', 400: '#7fb7ff', 500: '#458fe8', 600: '#2f74cb', 700: '#245ca4', 800: '#214d85', 900: '#1f426d', 950: '#142746' },
            surface: MONO_WIDE_SKY_SURFACE.dark,
        },
      },
      {
        label: 'Mono Wide 02',
        light: {
            primary: { 50: '#fafcff', 100: '#ebf4ff', 200: '#d1e5ff', 300: '#aaccff', 400: '#74a8ff', 500: '#3f83e0', 600: '#2d69bf', 700: '#25549c', 800: '#21457f', 900: '#1f3a69', 950: '#141f40' },
            surface: MONO_WIDE_SKY_SURFACE.light,
        },
        dark: {
            primary: { 50: '#fafcff', 100: '#ebf4ff', 200: '#d1e5ff', 300: '#aaccff', 400: '#74a8ff', 500: '#3f83e0', 600: '#2d69bf', 700: '#25549c', 800: '#21457f', 900: '#1f3a69', 950: '#141f40' },
            surface: MONO_WIDE_SKY_SURFACE.dark,
        },
      },
      {
        label: 'Mono Wide 03',
        light: {
            primary: { 50: '#f9fbfe', 100: '#eaf2fb', 200: '#cfdef5', 300: '#a9c2eb', 400: '#6d9cda', 500: '#3f76bf', 600: '#325f9f', 700: '#294d81', 800: '#243f69', 900: '#1f3557', 950: '#121e32' },
            surface: MONO_WIDE_SKY_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f9fbfe', 100: '#eaf2fb', 200: '#cfdef5', 300: '#a9c2eb', 400: '#6d9cda', 500: '#3f76bf', 600: '#325f9f', 700: '#294d81', 800: '#243f69', 900: '#1f3557', 950: '#121e32' },
            surface: MONO_WIDE_SKY_SURFACE.dark,
        },
      },
      {
        label: 'Mono Wide 04',
        light: {
            primary: { 50: '#f8fbfd', 100: '#e7f1f9', 200: '#cadef1', 300: '#a0c1e2', 400: '#6697ca', 500: '#3d6ea9', 600: '#31598d', 700: '#294970', 800: '#233d5c', 900: '#1e344d', 950: '#121f31' },
            surface: MONO_WIDE_SKY_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f8fbfd', 100: '#e7f1f9', 200: '#cadef1', 300: '#a0c1e2', 400: '#6697ca', 500: '#3d6ea9', 600: '#31598d', 700: '#294970', 800: '#233d5c', 900: '#1e344d', 950: '#121f31' },
            surface: MONO_WIDE_SKY_SURFACE.dark,
        },
      },
      {
        label: 'Mono Wide 05',
        light: {
            primary: { 50: '#f7fafc', 100: '#e5eff7', 200: '#c5dced', 300: '#98bbdb', 400: '#5e91bf', 500: '#37689c', 600: '#2d557f', 700: '#264565', 800: '#213952', 900: '#1d3045', 950: '#101a29' },
            surface: MONO_WIDE_NIGHT_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f7fafc', 100: '#e5eff7', 200: '#c5dced', 300: '#98bbdb', 400: '#5e91bf', 500: '#37689c', 600: '#2d557f', 700: '#264565', 800: '#213952', 900: '#1d3045', 950: '#101a29' },
            surface: MONO_WIDE_NIGHT_SURFACE.dark,
        },
      },
      {
        label: 'Mono Wide 06',
        light: {
            primary: { 50: '#f6f9fc', 100: '#e2edf5', 200: '#bed6e9', 300: '#8fb3d4', 400: '#5688b6', 500: '#31618f', 600: '#294f77', 700: '#22405f', 800: '#1d354f', 900: '#192d42', 950: '#0d1725' },
            surface: MONO_WIDE_NIGHT_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f6f9fc', 100: '#e2edf5', 200: '#bed6e9', 300: '#8fb3d4', 400: '#5688b6', 500: '#31618f', 600: '#294f77', 700: '#22405f', 800: '#1d354f', 900: '#192d42', 950: '#0d1725' },
            surface: MONO_WIDE_NIGHT_SURFACE.dark,
        },
      },
      {
        label: 'Mono Wide 07',
        light: {
            primary: { 50: '#f5f8fb', 100: '#e0ebf4', 200: '#bad2e5', 300: '#86abc9', 400: '#4f80aa', 500: '#2d5c87', 600: '#254b71', 700: '#1f3d5b', 800: '#1a324a', 900: '#172a3f', 950: '#0b1521' },
            surface: MONO_WIDE_NIGHT_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f5f8fb', 100: '#e0ebf4', 200: '#bad2e5', 300: '#86abc9', 400: '#4f80aa', 500: '#2d5c87', 600: '#254b71', 700: '#1f3d5b', 800: '#1a324a', 900: '#172a3f', 950: '#0b1521' },
            surface: MONO_WIDE_NIGHT_SURFACE.dark,
        },
      },
      {
        label: 'Mono Wide 08',
        light: {
            primary: { 50: '#f4f8fb', 100: '#dde9f2', 200: '#b4cde0', 300: '#7ea7c6', 400: '#49799f', 500: '#295679', 600: '#214765', 700: '#1c3952', 800: '#182f44', 900: '#15273a', 950: '#09131f' },
            surface: MONO_WIDE_NIGHT_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f4f8fb', 100: '#dde9f2', 200: '#b4cde0', 300: '#7ea7c6', 400: '#49799f', 500: '#295679', 600: '#214765', 700: '#1c3952', 800: '#182f44', 900: '#15273a', 950: '#09131f' },
            surface: MONO_WIDE_NIGHT_SURFACE.dark,
        },
      },
      {
        label: 'Mono Wide 09',
        light: {
            primary: { 50: '#f2f7fb', 100: '#dae8f2', 200: '#aec9de', 300: '#73a0bf', 400: '#3f7398', 500: '#23516f', 600: '#1d445f', 700: '#18374d', 800: '#142d40', 900: '#112536', 950: '#070f19' },
            surface: MONO_WIDE_NIGHT_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f2f7fb', 100: '#dae8f2', 200: '#aec9de', 300: '#73a0bf', 400: '#3f7398', 500: '#23516f', 600: '#1d445f', 700: '#18374d', 800: '#142d40', 900: '#112536', 950: '#070f19' },
            surface: MONO_WIDE_NIGHT_SURFACE.dark,
        },
      },
      {
        label: 'Mono Wide 10',
        light: {
            primary: { 50: '#f1f6fa', 100: '#d7e6f0', 200: '#a8c6da', 300: '#6998b8', 400: '#376a90', 500: '#1f4a68', 600: '#1a3e58', 700: '#153248', 800: '#12293c', 900: '#0f2232', 950: '#050d16' },
            surface: MONO_WIDE_NIGHT_SURFACE.light,
        },
        dark: {
            primary: { 50: '#f1f6fa', 100: '#d7e6f0', 200: '#a8c6da', 300: '#6998b8', 400: '#376a90', 500: '#1f4a68', 600: '#1a3e58', 700: '#153248', 800: '#12293c', 900: '#0f2232', 950: '#050d16' },
            surface: MONO_WIDE_NIGHT_SURFACE.dark,
        },
      },
    ],
  },
} as const satisfies Record<string, ThemeFamilyCatalogEntry>;

export type ThemeFamily = keyof typeof THEME_PRIMARY_THEME_CATALOG;

export interface ThemeFamilyOption {
  id: ThemeFamily;
  label: string;
  descriptionPrefix: string;
  themesCount: number;
}

export interface ThemeOption {
  id: string;
  family: ThemeFamily;
  familyLabel: string;
  label: string;
  description: string;
  themes: ThemeModesConfig;
}

function createThemeOptions(
  familyId: ThemeFamily,
  family: ThemeFamilyCatalogEntry
): ThemeOption[] {
  return family.themes.map((theme, index) => {
    const sequence = `${index + 1}`.padStart(2, '0');

    return {
      id: `${familyId}-${sequence}`,
      family: familyId,
      familyLabel: family.label,
      label: theme.label ?? `${family.label} ${sequence}`,
      description: theme.description ?? `${family.descriptionPrefix} ${sequence}`,
      themes: resolveThemeModes(theme),
    };
  });
}

const THEME_PRIMARY_THEME_CATALOG_ENTRIES = Object.entries(
  THEME_PRIMARY_THEME_CATALOG
) as [ThemeFamily, ThemeFamilyCatalogEntry][];

export const THEME_PRIMARY_THEME_FAMILY_OPTIONS: readonly ThemeFamilyOption[] =
  THEME_PRIMARY_THEME_CATALOG_ENTRIES.map(([id, family]) => ({
    id,
    label: family.label,
    descriptionPrefix: family.descriptionPrefix,
    themesCount: family.themes.length,
  }));

export const THEME_PRIMARY_THEME_OPTIONS_BY_FAMILY = THEME_PRIMARY_THEME_CATALOG_ENTRIES.reduce(
  (accumulator, [id, family]) => {
    accumulator[id] = createThemeOptions(id, family);
    return accumulator;
  },
  {} as Record<ThemeFamily, readonly ThemeOption[]>
);

export const THEME_PRIMARY_THEME_OPTIONS: readonly ThemeOption[] =
  THEME_PRIMARY_THEME_FAMILY_OPTIONS.flatMap((family) => THEME_PRIMARY_THEME_OPTIONS_BY_FAMILY[family.id]);

export const DEFAULT_PRIMARY_THEME_ID = 'originals-01';
